import { useMemo } from 'react';
import {
  Dialog,
  DialogContentHighPriority,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle2, XCircle, FileText } from 'lucide-react';

interface ItemIncompleto {
  item: string;
  erro: string;
}

interface ItemComErros {
  numeroItem: string;
  erros: string[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itensIncompletos: ItemIncompleto[];
  totalItens: number;
  itensCompletos: number;
  onCompletarItens: () => void;
}

export function AlertaItensIncompletosModal({
  open,
  onOpenChange,
  itensIncompletos,
  totalItens,
  itensCompletos,
  onCompletarItens,
}: Props) {
  // Agrupar erros por item
  const itensAgrupados = useMemo(() => {
    const mapa = new Map<string, string[]>();
    
    itensIncompletos.forEach(({ item, erro }) => {
      if (!mapa.has(item)) {
        mapa.set(item, []);
      }
      mapa.get(item)!.push(erro);
    });
    
    return Array.from(mapa.entries()).map(([numeroItem, erros]) => ({
      numeroItem,
      erros
    }));
  }, [itensIncompletos]);

  const totalItensUnicos = itensAgrupados.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContentHighPriority className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            Não é possível fechar o pedido
          </DialogTitle>
          <DialogDescription>
            Alguns itens estão incompletos e precisam ser finalizados
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* RESUMO */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-muted rounded-lg">
              <p className="text-2xl font-bold">{totalItens}</p>
              <p className="text-xs text-muted-foreground">Total de Itens</p>
            </div>
            <div className="text-center p-3 bg-success/5 dark:bg-success/20 rounded-lg">
              <p className="text-2xl font-bold text-success dark:text-success">{itensCompletos}</p>
              <p className="text-xs text-muted-foreground">
                <CheckCircle2 className="w-3 h-3 inline mr-1" />
                Completos
              </p>
            </div>
            <div className="text-center p-3 bg-danger/5 dark:bg-danger/20 rounded-lg">
              <p className="text-2xl font-bold text-danger dark:text-danger">{totalItensUnicos}</p>
              <p className="text-xs text-muted-foreground">
                <XCircle className="w-3 h-3 inline mr-1" />
                Incompletos
              </p>
            </div>
          </div>

          {/* ALERTA */}
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {totalItensUnicos === 1 
                ? 'Complete os campos obrigatórios do item abaixo para poder fechar o pedido.'
                : `Complete os campos obrigatórios dos ${totalItensUnicos} itens abaixo para poder fechar o pedido.`
              }
            </AlertDescription>
          </Alert>

          {/* LISTA DE ITENS INCOMPLETOS */}
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {itensAgrupados.map((itemComErros, index) => (
              <div key={index} className="border rounded-lg overflow-hidden">
                {/* HEADER DO ITEM */}
                <div className="bg-muted/50 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <span className="font-semibold">{itemComErros.numeroItem}</span>
                  </div>
                  <Badge variant="destructive">
                    {itemComErros.erros.length} {itemComErros.erros.length === 1 ? 'erro' : 'erros'}
                  </Badge>
                </div>
                
                {/* LISTA DE ERROS DO ITEM */}
                <div className="p-4 space-y-2">
                  {itemComErros.erros.map((erro, erroIndex) => (
                    <div key={erroIndex} className="flex items-start gap-2 text-sm">
                      <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{erro}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={onCompletarItens}>
            Completar Itens Agora
          </Button>
        </DialogFooter>
      </DialogContentHighPriority>
    </Dialog>
  );
}
